/* eslint-disable camelcase */
import { SaleTypeEnum } from './common'

export const EventDeliveryType = 'MOP'

export const GtmEventName = {
  PAGE_VIEW: 'page_view',
  GO_LOGIN: 'go_login',
  LOGIN: 'login',
  LOGIN_ERROR: 'login_error',
  LOGOUT: 'logout',
  MENU_HOME: 'menu_home',
  MENU_OFFERS: 'menu_offers',
  MENU_LOYALTY: 'menu_loyalty',
  MENU_BILL: 'menu_bill',
  MENU: (buttonType: string) => {
    switch (buttonType) {
      case 'aop-coupon':
        return GtmEventName.MENU_OFFERS
      case 'loyalty':
        return GtmEventName.MENU_LOYALTY
      case 'bill':
        return GtmEventName.MENU_BILL
      default:
        return GtmEventName.MENU_HOME
    }
  },
  GET_BILL: 'get_bill',
  GO_MYM: 'go_mym',
  MYM_HOME: 'mym_home',
  VIEW_ITEM_LIST: 'view_item_list',
  SELECT_ITEM: 'select_item',
  VIEW_ITEM: 'view_item',
  CUSTOMIZE_ITEM: 'customize_item',
  SELECT_COMPONENT: 'select_component',
  ADDITIONAL_COMPONENT: 'additional_component',
  CUSTOMIZE_INGREDIENTS: 'customize_ingredients',
  SAVE_INGREDIENTS: 'save_ingredients',
  ADD_TO_CART: 'add_to_cart',
  GO_CART: 'go_cart',
  VIEW_CART: 'view_cart',
  REMOVE_FROM_CART: 'remove_from_cart',
  APPLY_COUPON: 'apply_coupon',
  CONTINUE_ORDERING: 'continue_ordering',
  BEGIN_CHECKOUT: 'begin_checkout',
  ADD_SHIPPING_INFO: 'add_shipping_info',
  ADD_PAYMENT_INFO: 'add_payment_info',
  ORDER_FAILED: 'order_failed',
  PURCHASE: 'purchase',
  FRONT_PURCHASE: 'front_purchase',
  SELECT_PICKUP_RESTAURANT: 'select_pickup_restaurant',
  SELECT_PICKUP_TAKE_AWAY: 'select_pickup_take_away',
  SELECT_PICKUP: 'select_pickup',
  SELECT_PICKUP_SELECTOR: (type: string) =>
    type === SaleTypeEnum.EATIN
      ? GtmEventName.SELECT_PICKUP_RESTAURANT
      : GtmEventName.SELECT_PICKUP_TAKE_AWAY,
  CUSTOM_ERROR: 'custom_error',
  INACTIVITY_POPUP: 'inactivity_popup',
  INACTIVITY_EXIT: 'inactivity_exit',
  INOPERATIVE: 'inoperative',
  SELECT_FILTER: 'select_filter',
  SIN_LOGIN: 'sin_login',
  LOGIN_UPDATE: 'login_update',
  EXPERIMENT_VIEWED: 'experiment_viewed',
  LOGOUT_CONFIRMATION: 'logout_confirmation',
  GO_BACK: 'go_back',
  EMPTY_CART: 'empty_cart',
  HOME_BUTTON: (buttonLabel: string): `home_${string}` => `home_${buttonLabel}`,
  VIEW_PROMOTION: 'view_promotion', // for banner block
  SELECT_PROMOTION: 'select_promotion', // for banner block
  GET_ONLINE: 'get_online',
  SELECT_SIZE: 'select_size',
  EMPTY_CART_CONFIRMATION: 'empty_cart_confirmation',
  VALIDATION_WARNING: 'validation_warning',
  VALIDATION_SUMMIT: 'validation_summit',
  SEND_CODE: 'send_code',
  CLICK_CATEGORY: 'click_category',
  CONTINUE_GUEST: 'continue_guest',
  ORDER_EXIT: 'order_exit',
  INTERACTION: 'interaction'
} as const

export type GTMEventType = (typeof GtmEventName)[keyof typeof GtmEventName] | `home_${string}`

export const CUSTOM_IM_ANALYTICS_ERRORS = {
  IM_API_ERROR: 3001,
  FORM_ERROR: 3002,
  LOGIN_ERROR: 3005
} as const

export const GTMContentGroup = {
  primary: {
    USER: 'user',
    HOME: 'home',
    ECOMMERCE: 'ecommerce',
    LOYALTY: 'loyalty',
    COUPONS: 'coupons'
  },
  secondary: {
    LOGIN: 'login',
    SELECT_LANGUAGE: 'select_language',
    HOME: 'home',
    ITEM_LIST: 'item_list',
    ITEM_DETAIL: 'item_detail',
    CART: 'cart',
    CROSS_SELLING: 'cross_selling',
    PAYMENT: 'payment',
    ORDER_FAILED: 'order_failed',
    ORDER_CANCELLED: 'order_cancelled',
    CONFIRMATION: 'confirmation',
    ORDER: 'order',
    ERROR: 'error',
    POINTS: 'points',
    EMPTY_CART: 'empty_cart',
    COUPON_DETAIL: 'coupon_detail',
    INOPERATIVE: 'inoperative',
    INACTIVITY: 'inactivity'
  },
  tertiary: {
    WELCOME: 'welcome',
    ERROR: 'error',
    ITEM_ID: (itemId: string) => `${itemId}`,
    ID_RFM: (idRfm: string) => `${idRfm}`,
    EMPTY: 'empty',
    // saleType
    SELECT_DELIVERY: 'select_delivery',
    // checkout
    SELECT_METHOD: 'select_method',
    SELECT_METHOD_TABLE: 'select_method/table',
    SELECT_METHOD_TABLE_NUMBER: 'select_method/table_number',
    ORDER_NUMBER: 'order_number',
    THANK_YOU: 'thank_you',
    // others
    BANNER: 'banner', // TO DO
    INFO: 'info',
    VALIDATION_INACTIVITY: 'validation/inactivity',
    UPGRADE: 'upgrade',
    CHANGE: 'change',
    BEFORE: 'before',
    AFTER: 'after',
    ADK_MANUAL: 'adk_manual',
    VERIFY: 'verify',
    VERIFIED: 'verified'
  }
} as const

/**
 * Types
 *  */

/* PageView */

export type GTMViewContentGroup = {
  cg?: string
  cg2?: string
  cg3?: string
  pageTitle?: string
  linkUrl?: string
}

export const EventOrigin = {
  DATALAYER: 'dl',
  ADK: 'ADK'
} as const

export const EventQRType = {
  COUPON: 'coupon',
  USER_LOGIN: 'user_login'
} as const

export const EventPickupType = {
  TABLE: 'table',
  COUNTER: 'counter'
} as const

export const ItemSize = {
  SMALL: 'S',
  MEDIUM: 'M',
  LARGE: 'L',
  UNIQUE: 'unica'
} as const

export const LoginMethod = {
  QR_IDENTIFICATION: 'qr_identification',
  COUPON: 'coupon'
} as const

export const GTMLoginType = {
  SOFT_LOGIN: 'soft_login',
  IM_LOGIN: 'im_login'
} as const

export const QrTypes = {
  COUPON: 'coupon',
  USER_LOGIN: 'user_login'
} as const

export const LogoutType = {
  MANUAL: 'manual',
  INACTIVITY: 'inactivity',
  UNAUTHORIZED: 'unauthorized',
  CANCELLATION: 'cancellation',
  PURCHASED: 'purchased',
  LOGIN_UPDATE: 'login_update',
  INOPERATIVE: 'inoperative'
} as const

export const LoginUpdateType = {
  UPGRADE: 'upgrade',
  USER_CHANGE: 'user_change',
  SAME_USER: 'same_user'
} as const

export type ItemSizeType = (typeof ItemSize)[keyof typeof ItemSize]

export class GTMEcommerceItem {
  index?: number = undefined
  currency?: string
  item_name?: string
  item_id?: string
  price?: number
  quantity?: number
  coupon?: string = undefined
  coupons?: string = undefined
  discount?: number = 0
  /* Loyalty */
  loyalty?: boolean = undefined
  points?: number = 0
  item_available?: boolean = undefined
  item_list_name?: string = undefined
  item_category?: string = undefined
  pre_order?: boolean = undefined
  is_combo?: boolean = undefined
  item_variant?: ItemSizeType = undefined // 'L' | 'M' | 'S' | 'unica'
  /* Customization */
  // Choice option inside CHOICE group
  component_name?: string = undefined
  component_id?: string = undefined
  // string of ingredients edited
  ingredients_edited?: string = undefined
  /* Promotion (banner) */
  promotion_id?: string = undefined
  promotion_name?: string = undefined
}

export class GtmEcommerce {
  transaction_id?: string = undefined
  value?: number = undefined
  payment_type?: string = undefined
  payment_type2?: string = undefined
  tax?: number = undefined
  shipping?: number = undefined
  discount?: number = undefined
  tip?: number = undefined
  bill?: boolean = undefined
  currency?: string = undefined
  coupon?: string = undefined
  coupon2?: string = undefined
  coupon3?: string = undefined
  delivery_type?: string = EventDeliveryType
  shipping_tier?: string = undefined
  restaurant_code?: string = undefined
  restaurant_type?: string = 'restaurant'
  loyalty?: boolean = undefined
  points?: number = undefined
  points_spent?: number = undefined
  promotion_name?: string = undefined
  promotion_id?: string = undefined
  items?: GTMEcommerceItem[] = undefined
  pickup_type?: string = undefined
  item_list_name?: string = undefined
  cancelation_type?: string = undefined
  cancelation_warning?: string = undefined
  pre_order?: boolean = undefined
  filters_applied?: string = undefined
  selected_size?: string = undefined // 'large' | 'medium' | 'small' | 'none'
  // banner (view_promotion)
  link_url?: string = undefined
  creative_slot?: string = undefined
  type_promotion?: string = undefined // 'informative' | 'category' | 'plu' | 'free'
  // click_category
  category_name?: string = undefined
  category_index?: number = undefined
}

export class GTMEvent {
  event: GTMEventType
  event_origin?: string = EventOrigin.DATALAYER
  // page view
  content_group?: string = undefined
  content_group2?: string = undefined
  content_group3?: string = undefined
  page_title?: string = undefined
  // page_location override
  page_custom_location?: string = undefined
  // user
  user_id?: string = undefined
  country?: string
  is_loyalty?: boolean = undefined
  loyalty_points?: number = undefined
  user_type?: string = undefined
  login_type?: string = undefined
  is_logged?: boolean = undefined
  is_employee?: boolean = undefined
  // other properties
  content_type?: string = undefined
  method?: string = undefined
  error_code?: string = undefined
  error_type?: string = undefined
  form_name?: string = undefined
  search_term?: string = undefined
  outbound?: boolean = undefined
  link_url?: string = undefined
  warning_type?: string = undefined
  offer_code?: string = undefined
  offer_id?: string = undefined
  offer_name?: string = undefined
  comment?: string = undefined
  wt_step?: string = undefined
  wt_type?: string = undefined
  category_name?: string = undefined
  category_index?: number = undefined
  qr_type?: string = undefined
  group_id?: string = undefined
  kiosk_number?: string = undefined
  // login_update.type
  type?: string = undefined
  // GrowthBook experiment_viewed
  experiment_id?: string = undefined
  variation_id?: string = undefined
  // ecommerce
  ecommerce: GtmEcommerce = new GtmEcommerce()
  // interaction
  component_name?: string = undefined
  link_text?: string = undefined

  constructor(ev: GTMEventType) {
    this.event = ev
  }
}

export const GTMProductTypes = {
  CROSS_SELLING_NBO: 'cross_selling_nbo',
  CROSS_SELLING_CMS: 'cross_selling_cms',
  CROSS_SELLING_MIX: 'cross_selling_mix'
} as const

export const GTMItemListName = {
  HOME_FEATURED_LOYALTY: 'home_featured_loyalty',
  LOYALTY_HOME: 'loyalty_home',
  HOME_RECOMMENDED_PRODUCTS: 'home_recommended_products',
  SIZE_OPTIONS: '_options',
  COUPON_PRODUCTS: '_coupons',
  HOME_BANNER: 'home_banner',
  OTHER: 'other'
} as const

export type GtmProductType = (typeof GTMProductTypes)[keyof typeof GTMProductTypes]

/* Interaction */
export const GTM_INTERACTION_COMPONENTS = {
  INACTIVITY: {
    componentName: 'inactivity_popup',
    primaryText: 'continue',
    secondaryText: 'cancel_order'
  }
} as const

